import { alpha, Box } from '@material-ui/core';
import clsx from 'clsx';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import TuneRoundedIcon from '@material-ui/icons/TuneRounded';

import { SButton, SETVIImage, SText } from '@setvi/shared/components';
import { DefaultImages } from '@setvi/shared/enums';
import { useDialog } from '@setvi/shared/providers';

import { Badge } from 'componentsV2';

import {
  PreviewZoomDialog,
  PREVIEW_ZOOM_DIALOG_ID,
  type PreviewPage
} from 'componentsV2/pdf-preview-dialog';
import { PrintOrderItem } from '../../../../types';
import {
  getItemPagesLabel,
  isItemCustomized,
  parseMachineNumbers
} from '../../../../utils';
import { useStyles } from './styles';

const CUSTOMIZED_COLOR = '#1E96FC';
const STATIC_COLOR = '#737680';

interface CartItemProps {
  item: PrintOrderItem;
  removing: boolean;
  actionsDisabled: boolean;
  onEdit: (item: PrintOrderItem) => void;
  onRemove: (id: number) => void;
}

export const CartItem = ({
  item,
  removing,
  actionsDisabled,
  onEdit,
  onRemove
}: CartItemProps) => {
  const classes = useStyles();
  const { openDialog, closeDialog } = useDialog();
  const { resource } = item;
  const customized = isItemCustomized(item);
  const color = customized ? CUSTOMIZED_COLOR : STATIC_COLOR;
  // Only assets that support QR codes, a cover page, or a location can be
  // customized.
  const canEdit =
    !!resource.HasQRCode || !!resource.HasCoverPage || !!resource.HasLocation;

  // Zoomable preview of what was added to the cart — the generated cover / QR
  // sheet / location PDFs (matching the customize dialog's preview pages), or
  // the item's own PDF for static items. Not the full resource viewer.
  const openPreview = () => {
    const pages: PreviewPage[] = [];
    if (item.addCover && item.coverPreviewUrl)
      pages.push({ label: 'Cover', pdfUrl: item.coverPreviewUrl });
    if (item.addQr && item.qrSheetUrl)
      parseMachineNumbers(item.qrNumbers).forEach((machine, index) =>
        pages.push({
          label: `MACHINE ${machine}`,
          pdfUrl: item.qrSheetUrl,
          pdfPage: index + 1
        })
      );
    if (item.addLocation && item.locationPreviewUrl)
      pages.push({ label: 'Location', pdfUrl: item.locationPreviewUrl });
    if (!pages.length) {
      const url = resource.DownloadURLWithSas ?? resource.DownloadURL;
      if (!url) return;
      pages.push({ label: resource.Name, pdfUrl: url });
    }

    openDialog(
      <PreviewZoomDialog
        title={resource.Name}
        resource={resource}
        pages={pages}
        onClose={() => closeDialog(PREVIEW_ZOOM_DIALOG_ID)}
      />,
      { id: PREVIEW_ZOOM_DIALOG_ID }
    );
  };

  return (
    <Box className={classes.card}>
      <SETVIImage
        alt={resource.Name}
        src={resource.ThumbURLWithSas}
        className={clsx(classes.thumb, classes.clickable)}
        errorImage={DefaultImages.RESOURCE}
        onClick={openPreview}
      />
      <Box className={classes.titleWrap}>
        <SText
          title={resource.Name}
          weight="medium"
          maxLines={2}
          className={classes.clickable}
          onClick={openPreview}
        />
      </Box>
      <Box className={classes.metaRow}>
        <Badge
          hideDot
          label={customized ? 'Customized' : 'Static'}
          backgroundColor={alpha(color, 0.15)}
          textColor={color}
          dotColor="transparent"
        />
        <SText title={getItemPagesLabel(item)} size="xs" fontColor="#5D7083" />
      </Box>

      <Box className={classes.actions}>
        {canEdit && (
          <SButton
            className={classes.actionButton}
            variant="outlined"
            disabled={actionsDisabled}
            startIcon={<TuneRoundedIcon />}
            onClick={() => onEdit(item)}>
            Edit
          </SButton>
        )}
        <SButton
          className={classes.actionButton}
          variant="outlined"
          loading={removing}
          disabled={actionsDisabled}
          startIcon={<CloseRoundedIcon />}
          onClick={() => onRemove(resource.ID)}>
          Remove
        </SButton>
      </Box>
    </Box>
  );
};
